/**
 * Position an open menu that is portaled to `document.body`.
 *
 * A menu rendered in place with `absolute` is clipped by any ancestor that
 * establishes a scroll container — which `Modal.Content` now does by default.
 * The fix every dropdown here needs is the same one `components/form/Dropdown`
 * has always had: render the menu into `document.body` with `position: fixed`
 * and drive its coordinates off the anchor's viewport rect. This hook is that
 * geometry, so the next dropdown reuses it instead of writing its own.
 *
 * It is NOT yet the single definition of menu geometry in this repo — four menus
 * use it (`components/form/Dropdown`, `USASFDivisionSelector`,
 * `FamilyAccountSearchInput`, `travel/HomeAirportsModal`), while
 * `components/ui/dropdown/Dropdown` and `teamBoard/AthleteActionMenu` still carry
 * their own portal code and their own z-index constants. Migrating those is
 * worthwhile; claiming it is already done is not.
 *
 * What it owns: the rect maths, the upward flip when there is not enough room
 * below, horizontal clamping so a wider-than-anchor menu stays on screen, and
 * recalculation on scroll and resize (including scrollable ancestors, which a
 * window listener alone does not see). What it does NOT own: rendering,
 * click-outside, or z-index — a portaled menu leaves its owner's DOM subtree, so
 * every caller must add the menu's own ref to its click-outside check or the
 * first click on an option closes the menu before the click lands.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

// useLayoutEffect warns during SSR; this hook only measures on the client, so fall
// back to useEffect on the server. Same shape as components/ui/dropdown/Dropdown.
// Exported so callers can re-measure before paint (see `recalculate` below) without
// importing their own copy of this dance.
export const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Ready to spread straight into a `style` prop, deliberately.
 *
 * A flipped menu is pinned by `bottom` and a menu below its anchor by `top`, and
 * exactly one of the two is ever set. Handing callers the separate numbers invited
 * them to apply half the shape — a `position: fixed` box with NEITHER falls back to
 * its static position, which for a `document.body` portal child parks the menu in
 * the top-left corner, detached from its trigger, and only in the flip case, so it
 * survives casual review. Spreading one object makes that unrepresentable rather
 * than merely tested for.
 */
export interface AnchoredMenuStyle {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
}

export interface AnchoredMenuPosition {
  /** Spread this into the menu's `style`; add only z-index and your own extras. */
  style: AnchoredMenuStyle;
  placement: 'above' | 'below';
}

interface UseAnchoredMenuPositionOptions {
  /** The element the menu hangs off — usually the trigger button or input row. */
  anchorRef: React.RefObject<HTMLElement | null>;
  /** Whether the menu is currently open. Position is only tracked while true. */
  open: boolean;
  /**
   * Roughly how tall the menu wants to be. Used to decide whether it fits below
   * the anchor, and as its height when flipped above. An estimate is fine; the
   * returned `maxHeight` is what actually bounds it.
   */
  estimatedHeight: number;
  /** Hard ceiling for the returned `maxHeight`. Defaults to `estimatedHeight`. */
  maxHeight?: number;
  /** Floor for the menu width when it should be wider than its anchor. */
  minWidth?: number;
  /** Space between the anchor and the menu. */
  gap?: number;
  /** Called when the anchor scrolls far enough out of view that the menu should close. */
  onDismiss?: () => void;
}

/** Keeps a portaled menu off the very edge of the viewport. */
const VIEWPORT_MARGIN = 8;

export function useAnchoredMenuPosition({
  anchorRef,
  open,
  estimatedHeight,
  maxHeight = estimatedHeight,
  minWidth = 0,
  gap = 8,
  onDismiss,
}: UseAnchoredMenuPositionOptions): {
  position: AnchoredMenuPosition;
  /**
   * Re-measure now. **Call this whenever your own layout moves the anchor while the
   * menu is open.**
   *
   * The hook measures when the menu opens and on scroll or resize. It does not watch
   * the anchor being pushed around by a sibling — neither the anchor's own size nor
   * the scroll container's changes, so no ResizeObserver would fire. (An
   * IntersectionObserver with a rootMargin, or reading the rect every frame while
   * open, could catch it; both cost more than asking the one component that knows.)
   * An in-place `absolute` menu never had this problem because it moved with the
   * field in normal flow; portaling is what makes re-measuring the caller's job.
   *
   * `HomeAirportsModal` is the worked example: picking an airport swaps an empty-state
   * block for a chip row ABOVE the search field, and its handler closes and refocuses
   * in one batch so `open` never transitions and the open-effect never re-runs. Left
   * alone, the panel keeps the old coordinates and floats away from the field.
   * Re-measure in a layout effect keyed on whatever changed, NOT inline in the
   * handler — inline runs before React has rendered the new layout, so it re-measures
   * the stale position.
   */
  recalculate: () => void;
} {
  const [position, setPosition] = useState<AnchoredMenuPosition>({
    style: { top: 0, left: 0, width: 0, maxHeight },
    placement: 'below',
  });

  // Held in a ref so the scroll/resize listeners never need re-subscribing when a
  // caller passes a fresh closure on every render. Assigned in an effect rather
  // than during render, which would be an impure render.
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  const recalculate = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor || typeof window === 'undefined') return;

    const rect = anchor.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Flip up only when the menu genuinely does not fit below AND there is more
    // room above — otherwise "not quite enough room" would push it to the worse side.
    const shouldFlipUp = spaceBelow < estimatedHeight + gap && spaceAbove > spaceBelow;

    const available = shouldFlipUp ? spaceAbove : spaceBelow;
    const width = Math.min(
      Math.max(rect.width, minWidth),
      window.innerWidth - VIEWPORT_MARGIN * 2
    );

    setPosition({
      style: {
      // A flipped menu is pinned by its BOTTOM edge, never by a top computed from
      // `estimatedHeight`. Two failures follow from getting this wrong, and both are
      // structurally impossible this way rather than clamped away:
      //   1. `top = rect.top - estimatedHeight - gap` goes NEGATIVE whenever the room
      //      above is smaller than the estimate, putting the menu off the top of the
      //      screen. For a 480px menu that band is ~176px wide of anchor positions at
      //      667px tall — and the division menu keeps its search box at the top, so
      //      those are the pixels that disappear.
      //   2. The rendered panel has `max-height` and no `height`, so it SHRINKS to its
      //      content. A top-pinned menu therefore detaches from its anchor the moment
      //      the list is filtered — bottom edge floats up, leaving a gap.
      // Pinning the bottom makes `estimatedHeight` decide only WHETHER to flip, never
      // where the menu sits. `maxHeight` below caps the height at `spaceAbove - gap -
      // 2*VIEWPORT_MARGIN`, so the top edge lands at 2*VIEWPORT_MARGIN (16px, measured)
      // however tall the content is — degenerate only when spaceAbove < 24.
      ...(shouldFlipUp
        ? { bottom: window.innerHeight - rect.top + gap }
        : { top: rect.bottom + gap }),
      // A menu wider than its anchor (or an anchor near the right edge) would run
      // off screen at the anchor's own left, which is what phone widths hit. The
      // floor is 0, not VIEWPORT_MARGIN, so an anchor already flush to the left edge
      // keeps its alignment instead of being nudged 8px out of line with its trigger.
        left: Math.max(0, Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN)),
        width,
        maxHeight: Math.max(0, Math.min(available - gap - VIEWPORT_MARGIN * 2, maxHeight)),
      },
      placement: shouldFlipUp ? 'above' : 'below',
    });
  }, [anchorRef, estimatedHeight, gap, maxHeight, minWidth]);

  // Measure before paint so the menu never shows for a frame at 0,0.
  useIsomorphicLayoutEffect(() => {
    if (open) recalculate();
  }, [open, recalculate]);

  useEffect(() => {
    if (!open || typeof window === 'undefined') return;

    let rafId = 0;

    // A window scroll listener does not fire for a scrolling ancestor, and every
    // one of these menus can sit inside a scrolling modal body or table.
    const scrollableAncestors: HTMLElement[] = [];
    let element = anchorRef.current?.parentElement;
    while (element) {
      const style = getComputedStyle(element);
      if (
        style.overflow === 'scroll' ||
        style.overflow === 'auto' ||
        style.overflowY === 'scroll' ||
        style.overflowY === 'auto'
      ) {
        scrollableAncestors.push(element);
      }
      element = element.parentElement;
    }

    const handleReposition = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const anchor = anchorRef.current;
        if (!anchor) return;

        const rect = anchor.getBoundingClientRect();
        const onScreen =
          rect.top >= -100 &&
          rect.left >= -100 &&
          rect.bottom <= window.innerHeight + 100 &&
          rect.right <= window.innerWidth + 100;

        // The viewport is not the only thing that can hide the anchor. Inside a
        // scrolling modal body the anchor is clipped away by ITS box long before it
        // leaves the screen, and a viewport-only test leaves the menu hanging in the
        // middle of the modal over unrelated rows with no visible owner. Check every
        // scrolling ancestor — the menu is portaled, so nothing clips it on the
        // anchor's behalf, and the NEAREST ancestor is not always the one doing the
        // clipping (a horizontally scrolling wrapper counts as a scroll container on
        // both axes, and its vertical box would miss the modal body outside it).
        //
        // A box with no width AND no height has not been measured — jsdom reports
        // every rect that way, and a real element can be mid-layout. That is not the
        // same as a genuinely collapsed container, whose zero height does hide its
        // contents and should dismiss. Skip only the unmeasured case.
        // A fixed-position anchor is USUALLY not clipped by an ancestor's overflow,
        // so an ancestor's box says nothing about whether it is visible and testing
        // it would dismiss a perfectly visible menu the moment anything scrolled.
        // Ancestor-clip dismissal is new behaviour for every existing `Dropdown` call
        // site, so the false-dismissal direction is a regression risk worth excluding.
        //
        // "Usually", not always, and the comment says so because the code cannot: an
        // ancestor with transform / filter / perspective / backdrop-filter /
        // contain:paint / will-change of those becomes the containing block for fixed
        // descendants, and then the anchor IS clipped by it. This guard would skip a
        // real clip in that case — the menu stays open when it could close, which is
        // exactly the pre-PR behaviour, so it fails safe. It also only inspects the
        // ANCHOR: a static anchor inside a fixed bar is not covered. No caller is in
        // either situation today — checked by reading every file that renders
        // `<Dropdown` from this module, deliberately stated as a scope rather than a
        // count, because two careful counts of that set disagreed and the number is
        // not the evidence. When one appears, walk anchor→ancestor and skip on the
        // first fixed element in between rather than widening this.
        const anchorIsFixed =
          scrollableAncestors.length > 0 && getComputedStyle(anchor).position === 'fixed';
        const clippedAway =
          !anchorIsFixed &&
          scrollableAncestors.some((el) => {
            const box = el.getBoundingClientRect();
            if (box.width === 0 && box.height === 0) return false;
            return rect.bottom <= box.top || rect.top >= box.bottom;
          });

        if (!onScreen || clippedAway) {
          onDismissRef.current?.();
        } else {
          recalculate();
        }
      });
    };

    window.addEventListener('scroll', handleReposition, { passive: true });
    window.addEventListener('resize', handleReposition);
    scrollableAncestors.forEach((el) =>
      el.addEventListener('scroll', handleReposition, { passive: true })
    );

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleReposition);
      window.removeEventListener('resize', handleReposition);
      scrollableAncestors.forEach((el) => el.removeEventListener('scroll', handleReposition));
    };
  }, [open, anchorRef, recalculate]);

  return { position, recalculate };
}

/**
 * z-index for a portaled menu. It must clear every app overlay including the
 * modal stack at 1000000-1000001 (`components/ui/modal`, `UnifiedAthleteModal`,
 * `EvaluationModal`); menus opened from inside those modals collided at 1000001
 * and rendered behind the owning modal's surface.
 *
 * TODO: fold into a central z-index token layer so this constant is defined once.
 */
export const ANCHORED_MENU_Z_INDEX = 2000001;
